/**
 * Impersonation — the session switch behind ImpersonateDialog (docs/DUPLICATION_AUDIT.md, C9).
 * One copy for the Campuses, Campus profile, Corporations and Corporate profile pages.
 *
 * The role picked decides the path:
 *  - Corporate level (weight > 99, the CampusDropDown threshold), or a corporation target:
 *    reloadSessionCorp's -99 path. ProcessSessionReload loads every campus of the corp, then
 *    clearFilters → toast → redirect. A campus target is pre-selected via the campus override.
 *  - Campus level on a campus target: the silent -1 reload, then wait for the session to show
 *    the new corp + campus, restrict acntList to that one campus (no campus dropdown), clear
 *    the filters and redirect. The Campus profile page used to take the corporate path here
 *    and left every campus of the corp in the list.
 *
 * Never confirmed in testing: it switches the signed-in session.
 */

import { getSession, setSession } from '../services/session.js';
// V1 helpers for impersonation (proven, session-aware)
import {
  iAmHigherAuthority, setRoleInfo, reloadSessionCorp, clearFilters,
} from '../libs.jsx';

/** Roles the signed-in user may impersonate, lowest weight first. */
export function impersonationRoles(userRoles, myRoleWeight) {
  return (userRoles || [])
    .filter((r) => iAmHigherAuthority(r.user_role_weight, myRoleWeight))
    .sort((a, b) => (a.user_role_weight || 0) - (b.user_role_weight || 0));
}

/**
 * Starts the switch. On success the page redirects to '/' once the session has reloaded.
 *
 * @param {{ corporateId: number, campusId?: number, roleWeight: number|string, userRoles: object[] }} target
 *        campusId — the campus to land on; omit (0) for a corporation
 * @returns {string|null} an error message to show, or null once the switch has started
 */
export function startImpersonation({ corporateId, campusId = 0, roleWeight, userRoles }) {
  const dData = getSession();
  if (!dData) return 'Session not found';

  const selectedWeight = parseInt(roleWeight, 10);
  const selectedRole = (userRoles || []).find((r) => r.user_role_weight === selectedWeight);
  setRoleInfo(selectedWeight, dData, selectedRole?.user_role_name || '');

  if (!campusId || iAmHigherAuthority(99, selectedWeight)) {
    reloadSessionCorp(corporateId, -99, dData.cognito, dData, campusId || 0);
    return null;
  }

  // Campus level: the silent path, so acntList can be restricted before the redirect.
  reloadSessionCorp(corporateId, -1, dData.cognito, dData, campusId);

  // Poll until the sessionLoad API updates the session, then restrict & redirect.
  const targetCorpId = String(corporateId);
  let attempts = 0;
  const maxAttempts = 25; // 25 × 200 ms = 5 s max

  const poll = setInterval(() => {
    attempts++;
    const updated = getSession();

    // Session is ready when ProcessSessionReload has saved the new corp/campus
    const sessionReady = updated
      && updated.corp?.toString() === targetCorpId
      && updated.acnt?.toString() === campusId.toString()
      && attempts > 2; // skip first ~400 ms to let API complete

    if (sessionReady || attempts >= maxAttempts) {
      clearInterval(poll);
      if (updated) {
        // Restrict campus list to just the target campus
        updated.acntList = [campusId];
        clearFilters(updated, false); // resets filters in-place, no save
        setSession(updated);           // persist once
      }
      window.location.href = '/';
    }
  }, 200);
  return null;
}
